import { serializeForClipboard, setClipboardData } from "@/components/tiptap/utils/handleCut";

import type { Slice } from "@tiptap/pm/model";
import type { EditorView } from "@tiptap/pm/view";

export async function writeSliceToClipboard(view: EditorView, slice: Slice): Promise<boolean> {
  const serialized = serializeForClipboard(view, slice);

  if (copyThroughEvent(serialized)) {
    return true;
  }

  if (!navigator.clipboard?.write) {
    return false;
  }

  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        "text/html": new Blob([serialized.dom.innerHTML], { type: "text/html" }),
        "text/plain": new Blob([serialized.text], { type: "text/plain" }),
      }),
    ]);
    return true;
  } catch {
    return false;
  }
}

/**
 * Hands the data to a `copy` event, as Cmd+C does. Mobile Safari loses math
 * with non-ASCII symbols (𝑥, α) written through `navigator.clipboard.write`.
 * Must run within the user's tap.
 */
function copyThroughEvent(serialized: ReturnType<typeof serializeForClipboard>) {
  let copied = false;

  const onCopy = (event: ClipboardEvent) => {
    copied = setClipboardData(event, serialized);
    // The editor's own copy handler would overwrite the data with its selection.
    if (copied) event.stopImmediatePropagation();
  };
  // WebKit disables the copy command with no selected range unless `beforecopy` is cancelled.
  const enableCopy = (event: Event) => event.preventDefault();

  window.addEventListener("beforecopy", enableCopy, true);
  window.addEventListener("copy", onCopy, true);
  try {
    document.execCommand("copy");
  } catch {
    // Falls back to the async clipboard.
  } finally {
    window.removeEventListener("beforecopy", enableCopy, true);
    window.removeEventListener("copy", onCopy, true);
  }

  return copied;
}
